export class AuthSessionError extends Error {
  constructor(category, message) {
    super(message);
    this.name = 'AuthSessionError';
    this.category = category;
  }
}

export function isUserFresh(user, now = Date.now) {
  return Boolean(
    user?.access_token &&
      typeof user.expires_at === 'number' &&
      user.expires_at * 1000 - now() > 60_000,
  );
}

function defaultLock(name, options, callback) {
  if (!navigator.locks) {
    throw new AuthSessionError('lock_unavailable', 'Web Locks are unavailable');
  }
  return navigator.locks.request(name, { mode: 'exclusive', signal: options.signal }, callback);
}

export function createAuthSessionCoordinator(options) {
  const now = options.now || Date.now;
  const timeoutMs = options.timeoutMs || 15_000;
  const lockName = `beedigit:oidc-refresh:v1:${options.authority.replace(/\/+$/, '')}:${
    options.clientId
  }`;
  let generation = 0;
  let operation = null;
  let signInInFlight = null;
  let lockUnavailableReported = false;
  const canUseLock = () => options.useWebLock && (options.lock || navigator.locks);
  const reportUnavailableLock = () => {
    if (!options.useWebLock || lockUnavailableReported) return;
    lockUnavailableReported = true;
    try {
      options.onLockUnavailable?.();
    } catch {
      // Diagnostics must not prevent in-context authentication.
    }
  };

  const refreshSession = (request) => {
    if (operation) return operation.caller;
    if (signInInFlight)
      return Promise.reject(
        new AuthSessionError('recovery_timeout', 'Authentication recovery is pending'),
      );
    const controller = new AbortController();
    const startedAt = Date.now();
    const deadline = Date.now() + timeoutMs;
    const currentGeneration = generation;
    let resolvePromise;
    let rejectPromise;
    const caller = new Promise((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });
    let callerSettled = false;
    let lockWaitMs = 0;
    let lockRequestedAt = startedAt;
    const reportResult = (result) => {
      try {
        options.onRefreshSettled?.(request, {
          ...result,
          durationMs: Math.max(0, Date.now() - startedAt),
          lockWaitMs,
        });
      } catch {
        // Diagnostics must not affect renewal results.
      }
    };
    const resolveCaller = (user) => {
      if (callerSettled) return;
      callerSettled = true;
      reportResult({ user });
      resolvePromise(user);
    };
    const rejectCaller = (error) => {
      if (callerSettled) return;
      callerSettled = true;
      reportResult({ error });
      rejectPromise(error);
    };
    const current = {
      phase: 'queued',
      controller,
      caller,
      settled: Promise.resolve(),
    };
    operation = current;
    controller.signal.addEventListener(
      'abort',
      () => {
        rejectCaller(
          new AuthSessionError('lock_timeout', 'Authentication lock request was cancelled'),
        );
      },
      { once: true },
    );
    const assertCurrent = () => {
      if (generation !== currentGeneration || controller.signal.aborted || Date.now() >= deadline) {
        throw new AuthSessionError('timeout', 'Authentication refresh timed out');
      }
    };
    const insideLock = async () => {
      assertCurrent();
      current.phase = 'active';
      assertCurrent();
      const stored = await options.getUser();
      assertCurrent();
      const hasNewerToken =
        request.reason === 'http_401' &&
        request.rejectedAccessToken &&
        stored?.access_token !== request.rejectedAccessToken;
      if (isUserFresh(stored, now) && (request.reason !== 'http_401' || hasNewerToken))
        return stored;
      assertCurrent();
      const refreshed = await options.signinSilent({
        timeoutMs: Math.max(1, deadline - Date.now()),
      });
      assertCurrent();
      if (!isUserFresh(refreshed, now)) {
        throw new AuthSessionError(
          'invalid_user',
          'Silent authentication returned an invalid user',
        );
      }
      return refreshed;
    };
    const timer = window.setTimeout(() => {
      const queued = current.phase === 'queued';
      if (queued && canUseLock()) lockWaitMs = Math.max(0, Date.now() - lockRequestedAt);
      if (queued) controller.abort();
      rejectCaller(
        new AuthSessionError(
          queued ? 'lock_timeout' : 'timeout',
          'Authentication refresh timed out',
        ),
      );
    }, timeoutMs);
    const underlying = Promise.resolve().then(() => {
      if (!canUseLock()) {
        reportUnavailableLock();
        return insideLock();
      }
      lockRequestedAt = Date.now();
      return (options.lock || defaultLock)(lockName, { signal: controller.signal }, () => {
        lockWaitMs = Math.max(0, Date.now() - lockRequestedAt);
        return insideLock();
      });
    });
    current.settled = underlying
      .then(resolveCaller, rejectCaller)
      .then(
        () => undefined,
        () => undefined,
      )
      .finally(() => {
        window.clearTimeout(timer);
        current.phase = 'settled';
        if (operation === current) operation = null;
      });
    return caller;
  };

  const getValidUser = async () => {
    const current = await options.getUser();
    return isUserFresh(current, now) ? current : null;
  };
  const getValidAccessToken = async () => (await getValidUser())?.access_token || null;
  const ensureValidSession = async (reason) => (await getValidUser()) || refreshSession({ reason });
  const beginSignIn = (redirectOptions) => {
    if (signInInFlight) return signInInFlight;
    generation += 1;
    const pending = operation;
    if (pending?.phase === 'queued') pending.controller.abort();
    const controller = new AbortController();
    const deadline = Date.now() + timeoutMs;
    const recoveryGeneration = generation;
    const checkDeadline = () => {
      if (
        Date.now() >= deadline ||
        controller.signal.aborted ||
        generation !== recoveryGeneration
      ) {
        throw new AuthSessionError('recovery_timeout', 'Authentication recovery timed out');
      }
    };
    const recovery = async () => {
      if (pending?.phase === 'active') await pending.settled;
      checkDeadline();
      if (canUseLock()) {
        await (options.lock || defaultLock)(lockName, { signal: controller.signal }, async () => {
          checkDeadline();
          window.clearTimeout(timer);
          await options.signinRedirect(redirectOptions);
        });
      } else {
        reportUnavailableLock();
        window.clearTimeout(timer);
        await options.signinRedirect(redirectOptions);
      }
    };
    let timer;
    const timed = new Promise((_resolve, reject) => {
      timer = window.setTimeout(() => {
        controller.abort();
        reject(new AuthSessionError('recovery_timeout', 'Authentication recovery timed out'));
      }, timeoutMs);
    });
    signInInFlight = Promise.race([recovery(), timed]).then(
      () => {
        window.clearTimeout(timer);
      },
      (error) => {
        window.clearTimeout(timer);
        signInInFlight = null;
        throw error;
      },
    );
    return signInInFlight;
  };

  return {
    getValidUser,
    getValidAccessToken,
    ensureValidSession,
    refreshSession,
    beginSignIn,
  };
}
